import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import wootConstants from 'dashboard/constants/globals';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';

// ¿el actor conserva acceso a la conversación tras una transferencia? Refleja el
// modelo del backend (Conversations::PermissionFilterService / ConversationPolicy):
// retienen acceso el admin nativo, los roles con conversation_manage/_advanced, y
// los MIEMBROS del team relevante — p.ej. el "Administrador Central", que es
// miembro de todos los teams. Se usa para decidir si, tras assignTeam/assignAgent,
// hay que cerrarle el panel al actor (rev62): SOLO si de verdad perdió el acceso.
export const actorKeepsConversationAccess = (rootGetters, teamId) => {
  const account = rootGetters.getCurrentAccount || {};
  const user = rootGetters.getCurrentUser || {};
  const role =
    account.role ||
    user.accounts?.find(acc => acc.id === account.id)?.role ||
    user.role;
  if (role === 'administrator') return true;

  const perms = account.permissions || [];
  if (
    perms.includes('conversation_manage_advanced') ||
    perms.includes('conversation_manage')
  ) {
    return true;
  }

  // 31-ago — `conversation_participating_manage` (rol "Agente Humano"): el backend
  // (ConversationPolicy#agent_can_access_conversation?) le da acceso SOLO a lo
  // ASIGNADO a él; la membresía de team NO abre la conversación. Por eso, al ceder la
  // conv deja de poder verla aunque sea de su team → hay que cerrar la caja. Sin esto
  // el frontend creía (por ser miembro del team) que conservaba acceso, dejaba la caja
  // colgada, y cada llamada de fondo sobre esa conv daba 403 → lluvia de toasts.
  if (perms.includes('conversation_participating_manage')) return false;

  const myTeams = rootGetters['teams/getMyTeams'] || [];
  return Boolean(teamId) && myTeams.some(team => team.id === teamId);
};

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatventas but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // SECURITY: si el backend nos rechaza con 401/403/404 al pedir esta
      // conversación, el usuario NO tiene acceso. Limpiamos selectedChatId
      // para que la UI deje de mostrar el shell vacío esperando broadcasts.
      // Antes esto se tragaba silenciosamente y la pantalla se quedaba
      // listening en ActionCable; cuando un admin cambiaba algo de la
      // conversación, el broadcast pintaba la conv en pantalla aunque el
      // usuario no debiera verla.
      const status = error?.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        commit(types.CLEAR_CURRENT_CHAT_WINDOW);
      }
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at, then by id as a deterministic
      // tie-breaker. rev9/P5 — created_at tiene resolución de segundo, así que
      // dos mensajes del mismo segundo (ej. "Recibido…" y la respuesta real)
      // quedaban con orden indefinido. El id es UUID v7 (ordenado por tiempo de
      // creación a ms), de modo que la comparación lexicográfica respeta el orden.
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        const byTime = new Date(a.created_at) - new Date(b.created_at);
        if (byTime !== 0) return byTime;
        if (a.id === b.id) return 0;
        return a.id < b.id ? -1 : 1;
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);
    if (data.dataFetched === undefined) {
      try {
        if (after) {
          // Deep-link a un mensaje puntual (?messageId=): conservamos el fetch
          // por cursor para posicionar la ventana alrededor de ese mensaje.
          await dispatch('fetchPreviousMessages', {
            after,
            before: data.messages[0].id,
            conversationId: data.id,
          });
        } else {
          // Open normal: pedimos la ventana MÁS RECIENTE (messages_latest, sin
          // cursor), que scopa las notas privadas por los team_ids ACTUALES del
          // lector y REEMPLAZA la ventana cacheada. Antes se paginaba con
          // `before: messages[0].id`, que nunca traía una nota MÁS NUEVA que el
          // último mensaje público — el caso de una nota dejada al transferir/
          // asignar la conv (evento más nuevo). Junto con la invalidación de
          // `dataFetched` en UPDATE_CONVERSATION, el RECEPTOR ve la nota sin F5.
          const {
            data: { payload },
          } = await MessageApi.getPreviousMessages({ conversationId: data.id });
          commit(types.SET_MISSING_MESSAGES, { id: data.id, data: payload });
        }
        data.dataFetched = true;
      } catch (error) {
        // Ignore error
      }
    }
  },

  // T2.1 — dejamos que el error del API se propague naturalmente (sin
  // try/catch que lo silencie). Antes este action envolvía con try/catch
  // que hacía `// Handle error` vacío: el caller creía que la asignación
  // fue exitosa y mostraba toast de éxito aunque el backend la rechazara
  // por "agent does not belong to team" y la conversación quedara sin
  // cambios (R4-1 del informe QA).
  assignAgent: async (
    { commit, dispatch, rootGetters },
    { conversationId, agentId }
  ) => {
    const response = await ConversationApi.assignAgent({
      conversationId,
      agentId,
    });
    dispatch('setCurrentChatAssignee', response.data);

    // rev62 — cerrar el panel del actor si CEDIÓ la conv a otro agente, para no
    // seguir viendo mensajes del nuevo assignee. El team de la conv NO cambia acá.
    // FIX (mismo criterio que assignTeam): cerramos SOLO si además PIERDE acceso
    // (no es admin/broad ni miembro del team de la conv). Antes `role !==
    // 'administrator'` cerraba la ventana del "Administrador Central" al reasignar
    // aunque conserve acceso → truncaba mensajes → notas privadas desaparecían.
    const currentUser = rootGetters.getCurrentUser;
    const newAssigneeId = response?.data?.id;
    const gaveItAway =
      newAssigneeId && currentUser?.id && newAssigneeId !== currentUser.id;
    const selectedChat = rootGetters.getSelectedChat;
    const convTeamId = selectedChat?.meta?.team?.id ?? selectedChat?.team_id;
    if (gaveItAway && !actorKeepsConversationAccess(rootGetters, convTeamId)) {
      commit(types.CLEAR_CURRENT_CHAT_WINDOW);
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  // T2.1 — mismo criterio que assignAgent: sin try/catch para que el error
  // del API llegue al caller.
  assignTeam: async (
    { commit, dispatch, rootGetters },
    { conversationId, teamId }
  ) => {
    const response = await ConversationApi.assignTeam({
      conversationId,
      teamId,
    });
    dispatch('setCurrentChatTeam', { team: response.data, conversationId });

    // rev62 — cerrar el panel del actor tras la transferencia para que el
    // ex-dueño no siga viendo mensajes de una conv que ya no puede atender.
    // OJO: `deselectIfAccessLost` (websocket) NO cubre al ACTOR, porque su
    // estado local ya se actualizó con la respuesta HTTP y no puede detectar el
    // "antes"; por eso el cierre tiene que decidirse acá.
    //
    // FIX: cerramos SOLO si el actor de verdad PIERDE acceso. Antes se usaba
    // `role !== 'administrator'` a secas → cerraba la ventana del "Administrador
    // Central" (miembro de todos los teams, conserva acceso) y, al deseleccionar,
    // SET_ALL_CONVERSATION truncaba el snapshot → las notas privadas desaparecían
    // hasta refrescar. `actorKeepsConversationAccess` respeta el modelo del
    // backend (admin / conversation_manage / miembro del nuevo team).
    if (!actorKeepsConversationAccess(rootGetters, teamId)) {
      commit(types.CLEAR_CURRENT_CHAT_WINDOW);
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit, dispatch },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      const finalStatus = updatedStatus || status;
      const finalSnoozedUntil =
        updatedSnoozedUntil === undefined ? snoozedUntil : updatedSnoozedUntil;
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: finalStatus,
        snoozedUntil: finalSnoozedUntil,
      });

      // Rev9 — resolver/reabrir cuenta como actividad: reflejo optimista para
      // que la conversación suba al tope de la lista (orden last_activity_at desc)
      // sin esperar el evento del backend. last_activity_at va en segundos (epoch).
      dispatch('updateConversationLastActivity', {
        conversationId,
        lastActivityAt: Math.floor(Date.now() / 1000),
      });

      if (finalStatus === wootConstants.STATUS_TYPE.RESOLVED) {
        await dispatch('notifications/removeByConversationId', {
          conversationId,
        });
      }
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || String(currentInbox) === String(inboxId);
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
